import { DEFAULT_MODE, getTokens } from './brands';

/**
 * Map a flat token set to the CSS custom properties consumed by plain CSS
 * files and any inline `var(--color-*)` usage. Names follow the EllaFin design
 * token vocabulary, mapped onto this site's structural roles:
 *   --color-primary   = accent / CTA (MUI primary.main)
 *   --color-secondary = dark brand surface (MUI secondary.main)
 *
 * @param {import('./brands').BrandTokens} t
 * @returns {Record<string, string>}
 */
const tokensToVars = (t) => ({
  '--color-primary': t.accent,
  '--color-primary-light': t.accentLight,
  '--color-primary-dark': t.accentDark,
  '--color-primary-text': t.accentText,

  '--color-secondary': t.surface,
  '--color-secondary-light': t.surfaceLight,
  '--color-secondary-dark': t.surfaceDark,
  '--color-secondary-text': t.surfaceText,

  '--color-accent': t.accent,
  '--color-accent-text': t.accentText,

  '--color-background': t.background,
  '--color-background-light': t.backgroundLight,
  '--color-heading': t.heading,

  '--color-card': t.card,
  '--color-card-text': t.cardText,

  '--color-text': t.text,
  '--color-text-muted': t.textMuted,
  '--color-text-light': t.textLight,

  '--color-muted': t.muted,
  '--color-muted-text': t.mutedText,

  '--color-border': t.border,
  '--color-input': t.input,
  '--color-focus-ring': t.focusRing,

  '--color-destructive': t.destructive,
  '--color-destructive-text': t.destructiveText,

  '--color-chart-1': t.charts[0],
  '--color-chart-2': t.charts[1],
  '--color-chart-3': t.charts[2],
  '--color-chart-4': t.charts[3],
  '--color-chart-5': t.charts[4],
});

/**
 * Apply the given brand to the document root: sets every `--color-*` custom
 * property plus `data-brand` / `data-mode` / `color-scheme` so CSS and the
 * native form controls follow the active theme.
 *
 * @param {string} brandKey
 */
export const applyCssVars = (brandKey) => {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  const vars = tokensToVars(getTokens(brandKey));
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  root.setAttribute('data-brand', brandKey);
  root.setAttribute('data-mode', DEFAULT_MODE);
  root.style.colorScheme = DEFAULT_MODE;
};
